import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select } from 'semantic-ui-react';
import UploadProfilePhoto from '../../components/helpers/guests/upload_photo';
import { GENDER_SELECT_OPTIONS } from '../../components/helpers/guests/genderOptions';
import { CIVIL_STATUS_SELECT_OPTIONS } from '../../components/helpers/guests/civilOptions';
import {
  birthdateForInput,
  birthdateInputBounds,
  emptyGuestFieldErrors,
  GUEST_FIELD_ERRORS,
  normalizeEmail,
  normalizeIdentification,
  normalizePhone,
  sanitizeGuestField,
  validateGuestFields,
} from '../../components/helpers/guests/guestFieldValidation';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from '../../components/helpers/recaptchaUtils';
import guestService from '../../services/api/guest';
import authService from '../../services/api/auth';
import Swal from 'sweetalert2';
import { searchSelectProps } from 'src/components/helpers';

const emptyForm = () => ({
  name: '',
  lastName: '',
  identification: '',
  email: '',
  phone: '',
  birthdate: '',
  gender: '',
  civil: '',
});

const EditGuestProfileModal = ({ open, onClose, guest, onUpdated }) => {
  const [image, set_image] = useState(null);
  const [editForm, set_editForm] = useState(emptyForm);
  const [fieldErrors, set_fieldErrors] = useState(emptyGuestFieldErrors);
  const [loading, set_loading] = useState(false);
  const getCaptchaToken = useRecaptchaToken('guest_update');
  const { min: minBirthdate, max: todayDate } = birthdateInputBounds();

  useEffect(() => {
    if (!open || !guest?.id) return;
    set_editForm({
      name: guest.name ?? '',
      lastName: guest.lastName ?? '',
      identification: normalizeIdentification(guest.identification),
      email: guest.email ?? '',
      phone: normalizePhone(guest.phone),
      birthdate: birthdateForInput(guest.birthdate),
      gender: guest.gender ?? '',
      civil: guest.civil ?? '',
    });
    set_image(null);
    set_fieldErrors(emptyGuestFieldErrors());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleEditField = (e, data = {}) => {
    const name = data.name ?? e.target.name;
    if (!name) return;
    const value = sanitizeGuestField(name, data.value ?? e.target.value);
    set_editForm((prev) => ({ ...prev, [name]: value }));
    set_fieldErrors((prev) => ({ ...prev, [name]: false }));
  };

  const canEditIdentification = Boolean(authService.getSession());

  const handleSaveProfile = async () => {
    const { name, lastName, email, phone, birthdate, gender, civil } = editForm;
    const identification = normalizeIdentification(
      (canEditIdentification ? editForm.identification : guest.identification) ?? ''
    );
    const phoneValue = normalizePhone(phone);
    const emailValue = normalizeEmail(email);
    if (!name?.trim() || !lastName?.trim() || !identification || !phoneValue) {
      Swal.fire('Completa nombre, apellidos, identificación y teléfono.');
      return;
    }

    const { errors, messages } = validateGuestFields({
      identification,
      email: emailValue,
      phone: phoneValue,
      birthdate,
    });
    if (messages.length) {
      set_fieldErrors(errors);
      Swal.fire({
        icon: 'warning',
        title: 'Revisa los datos',
        html: messages.map((message) => `<p>${message}</p>`).join(''),
      });
      return;
    }

    const captchaRequired = isCaptchaRequired();
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    set_loading(true);

    const onSuccess = async (response) => {
      if (response.data.success) {
        Swal.fire(response.data.message);
        if (typeof onUpdated === 'function') {
          try {
            await onUpdated({
              ...(response.data.updateFromUser || {}),
              identification,
              phone: phoneValue,
            });
          } catch (error) {
            // Parent screens handle lookup errors on their own.
          }
        }
        onClose();
        set_image(null);
      }
      set_loading(false);
    };

    const onError = (error) => {
      if (error.response?.data?.message) Swal.fire(error.response.data.message);
      set_loading(false);
    };

    if (image) {
      const data = new FormData();
      data.append('identification', identification);
      data.append('name', name.trim());
      data.append('lastName', lastName.trim());
      data.append('email', emailValue);
      data.append('phone', phoneValue);
      data.append('birthdate', birthdate || '');
      data.append('gender', gender || '');
      data.append('civil', civil || '');
      data.append('profile', image);
      if (captchaRequired) data.append('captcha', captcha);
      guestService.updateGuest(guest.id, data).then(onSuccess).catch(onError);
    } else {
      const body = {
        name: name.trim(),
        lastName: lastName.trim(),
        identification: identification,
        email: emailValue,
        phone: phoneValue,
        birthdate: birthdate || null,
        gender: gender || null,
        civil: civil || null,
      };
      if (captchaRequired) body.captcha = captcha;
      guestService.updateGuest(guest.id, body).then(onSuccess).catch(onError);
    }
  };

  const modalPhotoInputs = useMemo(
    () => ({ ...guest, photo: guest?.photo }),
    [guest?.id, guest?.photo]
  );

  const handleRequestClose = () => {
    if (!loading) onClose();
  };

  if (!guest?.id) return null;

  return (
    <Modal
      className="guest-profile-edit-modal"
      open={open}
      onClose={handleRequestClose}
      closeIcon={!loading}
      size="small"
    >
      <Modal.Header>Actualizar datos</Modal.Header>
      <Modal.Content scrolling>
        <div style={{ maxWidth: 320, margin: '0 auto 20px' }}>
          <UploadProfilePhoto
            key={`${guest?.id}-${guest?.photo}-${open}`}
            name="guestProfileModalPhoto"
            labelDescription="foto"
            errorInputs={{}}
            imageFile={null}
            filling
            inputs={modalPhotoInputs}
            set_image={set_image}
          />
        </div>
        <Form loading={loading} onSubmit={(e) => e.preventDefault()}>
          <Form.Field>
            <label>Nombres</label>
            <Input name="name" value={editForm.name} onChange={handleEditField} required />
          </Form.Field>
          <Form.Field>
            <label>Apellidos</label>
            <Input name="lastName" value={editForm.lastName} onChange={handleEditField} required />
          </Form.Field>
          <Form.Field error={fieldErrors.identification}>
            <label>Identificación</label>
            <Input
              name="identification"
              value={editForm.identification}
              onChange={handleEditField}
              required
              inputMode="numeric"
              maxLength={10}
              pattern="^[0-9]{6,10}$"
              disabled={!canEditIdentification}
            />
          </Form.Field>
          <Form.Field error={fieldErrors.email}>
            <label>Correo electrónico</label>
            <Input name="email" type="email" value={editForm.email} onChange={handleEditField} />
          </Form.Field>
          <Form.Field error={fieldErrors.phone}>
            <label>Teléfono</label>
            <Input
              name="phone"
              value={editForm.phone}
              onChange={handleEditField}
              required
              inputMode="numeric"
              maxLength={10}
              pattern="^3[0-9]{9}$"
            />
            {fieldErrors.phone && <small>{GUEST_FIELD_ERRORS.phone}</small>}
          </Form.Field>
          <Form.Field>
            <label>Género</label>
            <Select
              placeholder="Selecciona tu género"
              fluid
              {...searchSelectProps({
                name: 'gender',
                value: editForm.gender || '',
                handler: handleEditField,
                options: GENDER_SELECT_OPTIONS,
              })}
            />
          </Form.Field>
          <Form.Field>
            <label>Estado civil</label>
            <Select
              placeholder="Selecciona tu estado civil"
              fluid
              {...searchSelectProps({
                name: 'civil',
                value: editForm.civil || '',
                handler: handleEditField,
                options: CIVIL_STATUS_SELECT_OPTIONS,
              })}
            />
          </Form.Field>
          <Form.Field error={fieldErrors.birthdate}>
            <label>Cumpleaños</label>
            <Input
              name="birthdate"
              type="date"
              value={editForm.birthdate}
              onChange={handleEditField}
              min={minBirthdate}
              max={todayDate}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" disabled={loading} onClick={handleRequestClose}>
          Cancelar
        </Button>
        <Button type="button" primary loading={loading} onClick={handleSaveProfile}>
          Guardar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditGuestProfileModal;
